import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Signal, RefreshCw, CheckCircle2, XCircle, Wallet, Download } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatBs, formatDate } from "@/lib/format";

const STATUS_STYLE = {
  approved: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  failed: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  error: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  pending: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const RANGE_LABEL = { "1": "Hoy", "7": "Últimos 7 días", "30": "Últimos 30 días" };

export default function EkiipagoPage() {
  const [status, setStatus] = useState("all");
  const [txType, setTxType] = useState("recharge");
  const [range, setRange] = useState("1");
  const [exporting, setExporting] = useState(false);

  const { data: providersData } = useQuery({
    queryKey: ["ekiipago-providers"],
    queryFn: async () => (await api.get("/ekiipago/providers")).data,
  });
  const providers = providersData?.providers || [];

  const { data: balance } = useQuery({
    queryKey: ["ekiipago-balance", range],
    queryFn: async () => (await api.get("/ekiipago/balance", { params: { days: Number(range) } })).data,
    refetchInterval: 60000,
  });

  const { data: txData, refetch, isFetching } = useQuery({
    queryKey: ["ekiipago-transactions", status, txType, range],
    queryFn: async () => {
      const params = { days: Number(range) };
      if (status !== "all") params.status = status;
      if (txType !== "all") params.tx_type = txType;
      return (await api.get("/ekiipago/transactions", { params })).data;
    },
    refetchInterval: 30000,
  });
  const items = txData?.items || [];

  const providerName = (id) => providers.find((p) => String(p.id) === String(id))?.name || id;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { days: Number(range) };
      if (status !== "all") params.status = status;
      if (txType !== "all") params.tx_type = txType;
      const res = await api.get("/ekiipago/transactions/export.csv", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recargas_ekiipago_${range}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Exportación descargada");
    } catch {
      toast.error("No se pudo exportar el CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ekiipago-admin-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/15 grid place-items-center">
          <Signal className="text-primary" size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold leading-tight">Servicios · Ekiipago</h1>
          <p className="text-sm text-muted-foreground">Recargas, consultas y conciliación de la bolsa</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[170px]" data-testid="ekiipago-range-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1" data-testid="ekiipago-range-hoy">Hoy</SelectItem>
              <SelectItem value="7" data-testid="ekiipago-range-7d">Últimos 7 días</SelectItem>
              <SelectItem value="30" data-testid="ekiipago-range-30d">Últimos 30 días</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting} data-testid="ekiipago-export-csv-button">
            <Download size={16} className="mr-2" /> Exportar CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="ekiipago-refresh-button">
            <RefreshCw size={16} className={`mr-2 ${isFetching ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
      </div>

      {/* Balance / conciliación del rango */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4" data-testid="ekiipago-balance-total">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <Wallet size={16} /> Recargas · {RANGE_LABEL[range]} (Bs)
          </div>
          <div className="text-2xl font-bold font-mono tabular-nums">Bs {formatBs(balance?.day_total_bs || 0)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <CheckCircle2 size={16} className="text-emerald-500" /> Aprobadas
          </div>
          <div className="text-2xl font-bold">{balance?.approved_count ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <XCircle size={16} className="text-rose-500" /> Fallidas / error
          </div>
          <div className="text-2xl font-bold">{balance?.failed_count ?? 0}</div>
        </Card>
      </div>

      {balance?.by_provider?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {balance.by_provider.map((b) => (
            <Badge key={b.provider_name} variant="outline" className="gap-1.5">
              {b.provider_name}: <span className="font-mono">Bs {formatBs(b.total_bs)}</span> ({b.count})
            </Badge>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-3">
        <Select value={txType} onValueChange={setTxType}>
          <SelectTrigger className="w-[180px]" data-testid="ekiipago-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="recharge">Recargas</SelectItem>
            <SelectItem value="consulta">Consultas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="ekiipago-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estatus</SelectItem>
            <SelectItem value="approved">Aprobadas</SelectItem>
            <SelectItem value="failed">Fallidas</SelectItem>
            <SelectItem value="error">Error de red</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabla de transacciones */}
      <Card className="overflow-hidden">
        <Table data-testid="ekiipago-tx-table">
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Operadora</TableHead>
              <TableHead>Cuenta</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>N° Aprobación</TableHead>
              <TableHead>Estatus</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8" data-testid="ekiipago-empty">
                  Sin transacciones
                </TableCell>
              </TableRow>
            )}
            {items.map((t) => (
              <TableRow key={t.id} data-testid={`ekiipago-tx-row-${t.id}`}>
                <TableCell className="text-xs">{formatDate(t.created_at)}</TableCell>
                <TableCell>{t.provider_name || providerName(t.provider_id)}</TableCell>
                <TableCell className="font-mono text-xs">{t.cuenta}</TableCell>
                <TableCell className="text-right font-mono">Bs {formatBs(t.monto_bs)}</TableCell>
                <TableCell className="font-mono text-xs">{t.approval_number || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`uppercase text-[10px] ${STATUS_STYLE[t.status] || ""}`}>
                    {t.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
